import { formatAsBrDecimalNumber, formatAsBrNumber } from '~/prix'

export interface Detection {
  amplitude: number
  ratio: number
  count: number
  endAngle?: number
  start: string
  end: string
  startIndex: number
  endIndex: number
}

export interface MovingThresholdResult {
  type: 'movingThreshold'
  name: `detection.movingThreshold.${number}` | `layer.movingThreshold.${number}`
  multiplierThreshold: number
  method: 'mean' | 'median' | 'max' | 'min' | 'meanExceptQ1' | 'meanExceptQ3'
  amplitude: number
  windowSize: number
  detections: Detection[]
  isCurrentlyAbove: boolean
}
export const movingThresholdMethods = {
  mean: 'média',
  median: 'mediana',
  max: 'máximo',
  min: 'mínimo',
  meanExceptQ1: 'média excluindo 1º quartil',
  meanExceptQ3: 'média excluindo 3º quartil',
}

export interface FixedThresholdResult {
  type: 'fixedThreshold'
  name: `detection.fixedThreshold.${number}` | `layer.fixedThreshold.${number}`
  threshold: number
  amplitude: number
  detections: Detection[]
  isCurrentlyAbove: boolean
}

export interface SparseDataResult {
  type: 'sparseData'
  name: `detection.sparseData.${number}` | `layer.sparseData.${number}`
  medianBelow: number
  meanBelow: number
  maxBelow: number
  medianWindow: number
  meanWindow: number
  maxWindow: number
  detections: Detection[]
  isCurrentlySparse: boolean
  amplitude: number
}

export interface TTestOutbreaksResult {
  type: 'tTestOutbreaks'
  name:
    | `detection.pairedTTestOutbreaksDetection.${number}`
    | `layer.pairedTTestOutbreaksDetection.${number}`
  detections: Detection[]
  pValues: number[]
  amplitude: number
  alpha: number
  phase: number
  windowSize: number
  medianOutbreakInterval: number
  isCurrentlyOutbreak: boolean
}

export interface RiseComparisonDetectionResult {
  type: 'riseComparisonDetection'
  name: `detection.riseComparisonDetection.${number}` | `layer.riseComparisonDetection.${number}`
  multiplierThreshold: number
  comparator?: number
  windowSize: number
  previousWindowSize: number
  detections: Detection[]
  padding: number
  amplitude: number
}

export interface DropComparisonDetectionResult {
  type: 'dropComparisonDetection'
  name: `detection.dropComparisonDetection.${number}` | `layer.dropComparisonDetection.${number}`
  multiplierThreshold: number
  comparator?: number
  windowSize: number
  previousWindowSize: number
  detections: Detection[]
  padding: number
  amplitude: number
}

export interface CalendarPeriodMADResultLimits {
  lower: number
  upper: number
  median: number
  mad: number
  scale: number
  k: number
}

export interface CalendarPeriodMADDetectionResult {
  type: 'calendarPeriodMAD'
  name:
    | `detection.calendarPeriodMADDetection.${number}`
    | `layer.calendarPeriodMADDetection.${number}`
  periodType: 'day' | 'week' | 'fortnight' | 'month' | 'bimonth' | 'quarter' | 'quadrimester'
  focus: 'current' | 'previous'
  align: 'calendarDays' | 'businessDays'
  weekAnchor: 'sunday' | 'monday'
  isPartial: boolean
  historyUsed: number
  limits: CalendarPeriodMADResultLimits
  reference: { start: string; end: string; value: number }
  historyValues: number[]
  detections: Detection[]
  padding?: number
  amplitude: number
  minHistory?: number | null
  targetHistory?: number
  direction?: 'both' | 'increase' | 'decrease'
  numPeriods?: number
  aggregation?: 'sum' | 'mean' | 'median' | 'max' | 'min'
  limitMultiplier?: number
  useQuantileFallback?: boolean
  quantileFallback?: [number, number]
  minimumPartialObservations?: number
}

export interface CalendarPeriodComparisonDetectionResult {
  type: 'calendarPeriodComparison'
  name:
    | `detection.calendarPeriodComparisonDetection.${number}`
    | `layer.calendarPeriodComparisonDetection.${number}`
  periodType: 'week' | 'fortnight' | 'month' | 'year'
  focus: 'current' | 'previous'
  comparedTo: 'previous' | 'yearOverYear'
  aggregation: 'sum' | 'mean' | 'median' | 'max' | 'min' | string
  isPartialComparison: boolean
  daysCompared: number
  reference: { start: string; end: string; value: number }
  comparison: { start: string; end: string; value: number }
  referenceData: { y: number[] }
  comparisonData: { y: number[] }
  percentageChange: number
  percentageThreshold: number
  direction: 'both' | 'increase' | 'decrease'
  weekAnchor: 'sunday' | 'monday'
  detections: Detection[]
  amplitude: number
  padding?: number
}

export type SeasonalityType =
  | 'dayOfWeek'
  | 'dayOfMonth'
  | 'dayOfYear'
  | 'monthOfYear'
  | 'year'
  | 'holiday'
export interface ConditionalPolynomialCalendarSeasonalityResult {
  type: 'conditionalPolynomial'
  name: `layer.calendarSeasonality.${SeasonalityType}.${number}`
  amplitude: number
  degree: number
  coefficients: Record<string, number[]>
  meanAmplitudes: Record<string, number>
  counts: Record<string, number>
  endAngles: Record<string, number>
  start: Record<string, string>
  end: Record<string, string>
  isOptimized?: true
  changePoints?: string[]
}

export const dayOfWeek = {
  '0': 'domingo',
  '1': 'segunda-feira',
  '2': 'terça-feira',
  '3': 'quarta-feira',
  '4': 'quinta-feira',
  '5': 'sexta-feira',
  '6': 'sábado',
}

export const monthOfYear = {
  '01': 'janeiro',
  '02': 'fevereiro',
  '03': 'março',
  '04': 'abril',
  '05': 'maio',
  '06': 'junho',
  '07': 'julho',
  '08': 'agosto',
  '09': 'setembro',
  '10': 'outubro',
  '11': 'novembro',
  '12': 'dezembro',
}

export function seasonalityValuesToLabels(seasonalityType: SeasonalityType, values: string[]) {
  if (seasonalityType === 'dayOfWeek') {
    return values.map(value => dayOfWeek[value as keyof typeof dayOfWeek])
  } else if (seasonalityType === 'monthOfYear') {
    return values.map(value => monthOfYear[value as keyof typeof monthOfYear])
  } else {
    return values
  }
}

export interface PolynomialResult {
  type: 'polynomial'
  name: `layer.trend.${number}`
  amplitude: number
  degree: number
  coefficients: number[]
  count: number
  endAngle: number
  start: string
  end: string
  isOptimized?: true
  changePoints?: string[]
}

export interface HarmonicResult {
  type: 'harmonic'
  name: `layer.harmonicSeasonality.${number}`
  frequency: number
  amplitude: number
  start: string
  end: string
  length: number
  fftDefinition: {
    frequencyIndex: number
    amplitudeReal: number
    amplitudeImaginary: number
  }[]
  peaks: number
  isOptimized?: true
  changePoints?: string[]
}

export type TimeAnalysisDetectionResult =
  | MovingThresholdResult
  | FixedThresholdResult
  | SparseDataResult
  | TTestOutbreaksResult
  | RiseComparisonDetectionResult
  | DropComparisonDetectionResult
  | CalendarPeriodComparisonDetectionResult
  | CalendarPeriodMADDetectionResult
export type TimeAnalysisLayerResult =
  | ConditionalPolynomialCalendarSeasonalityResult
  | PolynomialResult
  | HarmonicResult

export type TimeAnalysisResult = TimeAnalysisDetectionResult | TimeAnalysisLayerResult
export type TimeAnalysisResultType = TimeAnalysisResult['type']

export function getLayerDescriptionsFromResult(result: TimeAnalysisResult) {
  if (
    result.name.startsWith('detection.movingThreshold.') ||
    result.name.startsWith('layer.movingThreshold.')
  ) {
    const movingThresholdResult = result as MovingThresholdResult
    return {
      name: 'Detecção por limiar móvel',
      description:
        'Esta camada realiza comparações entre cada valor da série e uma agregação dos valores anteriores, que pode ser feita através da média, mediana, máximo, entre outros métodos. Se um valor, após ser multiplicado por um fator pré-definido, exceder essa agregação, ocorre a detecção. Este método é particularmente eficaz para identificar subidas emergentes.',
      settings: `Acima da ${
        movingThresholdMethods[movingThresholdResult.method]
      } e multiplicador ${formatAsBrDecimalNumber(movingThresholdResult.multiplierThreshold)}.`,
    }
  } else if (
    result.name.startsWith('detection.fixedThreshold.') ||
    result.name.startsWith('layer.fixedThreshold.')
  ) {
    const fixedThresholdResult = result as FixedThresholdResult
    return {
      name: 'Detecção por limiar fixo',
      description:
        'Esta camada compara cada valor da série temporal com uma constante predeterminada. Se um valor exceder esse limiar fixo, a detecção é acionada. Esse método é muito útil para estabelecer limites máximos aceitáveis, conforme especificado por regras de negócio.',
      settings: `Igual ou superior a ${formatAsBrNumber(fixedThresholdResult.threshold)}.`,
    }
  } else if (
    result.name.startsWith('detection.sparseData.') ||
    result.name.startsWith('layer.sparseData.')
  ) {
    const sparseDataResult = result as SparseDataResult
    return {
      name: 'Detecção de dados esparsos',
      description:
        'Esta camada é útil quando uma série temporal apresenta poucas ocorrências, o que pode afetar a eficácia de outras camadas de detecção. Ela avalia a média, mediana e o máximo de uma janela de tempo anterior para determinar se a série está em um período de dados esparsos ou escassos. Assim, regras de negócio podem ser ajustadas levando em consideração essa característica da série.',
      settings: `Média menor que ${formatAsBrNumber(
        sparseDataResult.meanBelow,
      )} nos ${formatAsBrNumber(
        sparseDataResult.meanWindow,
      )} dias anteriores, mediana menor que ${formatAsBrNumber(
        sparseDataResult.medianBelow,
      )} nos ${formatAsBrNumber(
        sparseDataResult.medianWindow,
      )} dias anteriores e máxima menor que ${formatAsBrNumber(
        sparseDataResult.maxBelow,
      )} nos ${formatAsBrNumber(sparseDataResult.maxWindow)} dias anteriores.`,
    }
  } else if (
    result.name.startsWith('detection.riseComparisonDetection.') ||
    result.name.startsWith('layer.riseComparisonDetection.')
  ) {
    const riseComparisonDetectionResult = result as RiseComparisonDetectionResult
    return {
      name: 'Detecção por comparação de aumento',
      description: `Esta camada compara o comportamento recente da série temporal com um período anterior, identificando aumentos significativos nos valores. A detecção é acionada quando o valor médio da janela atual ultrapassa o da janela anterior por um multiplicador definido.`,
      settings: `Detecção comparando os ${riseComparisonDetectionResult.windowSize} dias mais recentes com os ${riseComparisonDetectionResult.previousWindowSize} dias anteriores, utilizando um multiplicador de ${riseComparisonDetectionResult.multiplierThreshold} para identificar aumentos significativos.`,
    }
  } else if (
    result.name.startsWith('detection.dropComparisonDetection.') ||
    result.name.startsWith('layer.dropComparisonDetection.')
  ) {
    const dropComparisonDetectionResult = result as DropComparisonDetectionResult
    return {
      name: 'Detecção por comparação de queda',
      description: `Essa camada compara duas janelas de tempo consecutivas para identificar quedas acentuadas nos dados. A detecção ocorre quando os valores recentes ficam significativamente abaixo dos anteriores, conforme definido por um multiplicador negativo.`,
      settings: `Detecção comparando os ${dropComparisonDetectionResult.windowSize} dias mais recentes com os ${dropComparisonDetectionResult.previousWindowSize} dias anteriores, utilizando um multiplicador de ${dropComparisonDetectionResult.multiplierThreshold} para identificar quedas significativas.`,
    }
  } else if (
    result.name.startsWith('detection.calendarPeriodMADDetection.') ||
    result.name.startsWith('layer.calendarPeriodMADDetection.')
  ) {
    const calendarPeriodMADResult = result as CalendarPeriodMADDetectionResult
    const periodLabels: Record<CalendarPeriodMADDetectionResult['periodType'], string> = {
      day: 'dia',
      week: 'semana',
      fortnight: 'quinzena',
      month: 'mês',
      bimonth: 'bimestre',
      quarter: 'trimestre',
      quadrimester: 'quadrimestre',
    }
    const directionLabel =
      calendarPeriodMADResult.direction === 'both' || !calendarPeriodMADResult.direction
        ? 'ambas as direções'
        : calendarPeriodMADResult.direction === 'increase'
          ? 'apenas aumentos'
          : 'apenas quedas'

    return {
      name: 'Detecção por período de calendário (MAD)',
      description:
        'Compara o valor agregado do período de calendário com a distribuição dos períodos anteriores, usando limites robustos pela mediana e pelo desvio absoluto mediano (MAD).',
      settings:
        `${calendarPeriodMADResult.focus === 'previous' ? 'Período anterior completo' : calendarPeriodMADResult.isPartial ? 'Período corrente parcial' : 'Período corrente completo'} ` +
        `por ${periodLabels[calendarPeriodMADResult.periodType]}, alinhado por ${calendarPeriodMADResult.align === 'businessDays' ? 'dias úteis' : 'dias corridos'}. ` +
        `Limites robustos via mediana ± k · 1,4826 · MAD (k=${formatAsBrDecimalNumber(calendarPeriodMADResult.limits.k)}). ` +
        `Direção: ${directionLabel}. História usada: ${formatAsBrNumber(calendarPeriodMADResult.historyUsed)} períodos.`,
    }
  } else if (
    result.name.startsWith('detection.calendarPeriodComparisonDetection.') ||
    result.name.startsWith('layer.calendarPeriodComparisonDetection.')
  ) {
    const calendarPeriodComparisonResult = result as CalendarPeriodComparisonDetectionResult
    const periodLabels: Record<CalendarPeriodComparisonDetectionResult['periodType'], string> = {
      week: 'semana',
      fortnight: 'quinzena',
      month: 'mês',
      year: 'ano',
    }
    const comparedToLabel =
      calendarPeriodComparisonResult.comparedTo === 'previous'
        ? 'período anterior'
        : 'mesmo período do ano anterior'
    const directionLabel =
      calendarPeriodComparisonResult.direction === 'both'
        ? 'ambas as direções'
        : calendarPeriodComparisonResult.direction === 'increase'
          ? 'apenas aumentos'
          : 'apenas quedas'

    return {
      name: 'Comparação de período de calendário',
      description:
        'Compara o agregado do período de referência com um período de comparação, calculando a variação percentual.',
      settings:
        `Período ${periodLabels[calendarPeriodComparisonResult.periodType]} ${calendarPeriodComparisonResult.focus === 'previous' ? 'fechado anterior' : calendarPeriodComparisonResult.isPartialComparison ? 'corrente parcial' : 'corrente completo'}, ` +
        `comparado a ${comparedToLabel}. ` +
        `Direção: ${directionLabel}. Limite: ${formatAsBrDecimalNumber(calendarPeriodComparisonResult.percentageThreshold * 100)}%. ` +
        `Dias comparados: ${formatAsBrNumber(calendarPeriodComparisonResult.daysCompared)}.`,
    }
  } else if (
    result.name.startsWith('detection.pairedTTestOutbreaksDetection.') ||
    result.name.startsWith('layer.pairedTTestOutbreaksDetection.')
  ) {
    const tTestOutbreaksResult = result as TTestOutbreaksResult
    return {
      name: 'Detecção por teste t de Student pareado com a própria série',
      description:
        'Esta camada emprega o teste T de Student pareado como um teste de hipóteses para avaliar se a série temporal é consistente consigo mesma ao longo do tempo. Limitada a uma janela de dias específica, a análise compara a série em seu estado atual com uma versão defasada, configurável em termos de intervalo temporal. Se a série deixa de ser estatisticamente consistente consigo mesma dentro dessa janela, a detecção é acionada.',
      settings: `Detecção no intervalo de confiança de ${formatAsBrNumber(
        (1 - tTestOutbreaksResult.alpha) * 100,
      )}%, janela de ${formatAsBrNumber(
        (result as TTestOutbreaksResult).windowSize,
      )} dias e defasagem de ${formatAsBrNumber(tTestOutbreaksResult.phase)} dias.`,
    }
  } else if (result.name.startsWith('layer.calendarSeasonality.dayOfWeek.')) {
    const calendarSeasonalityResult = result as ConditionalPolynomialCalendarSeasonalityResult
    return {
      name: 'Análise de sazonalidade de calendário por dia da semana',
      description:
        'Esta camada ajusta um polinômio de grau configurável separadamente para cada dia da semana, capturando e descrevendo as variações comportamentais específicas ao longo do tempo. Matematicamente, o modelo polinomial é adaptado para cada dia, permitindo uma análise detalhada das tendências sazonais que variam conforme o dia da semana. Este método é particularmente útil para identificar padrões recorrentes e diferenças entre os dias.',
      settings:
        `Foram utilizados polinômios de grau ${calendarSeasonalityResult.degree} para cada dia da semana.` +
        (calendarSeasonalityResult.isOptimized
          ? ' Foi utilizado otimizador para determinar pontos de mudança.'
          : ''),
    }
  } else if (result.name.startsWith('layer.calendarSeasonality.dayOfMonth.')) {
    const calendarSeasonalityResult = result as ConditionalPolynomialCalendarSeasonalityResult
    return {
      name: 'Análise de sazonalidade de calendário por dia do mês',
      description:
        'Esta camada aplica um polinômio de grau configurável individualmente para cada dia do mês, de 01 a 31, permitindo a descrição das variações comportamentais que ocorrem ao longo do tempo. A abordagem matemática consiste em ajustar um modelo polinomial que melhor se adapte aos padrões observados em cada dia específico do mês. Esse método é eficaz para explorar e entender as tendências sazonais que podem variar significativamente de um dia para outro dentro dos meses.',
      settings:
        `Foram utilizados polinômios de grau ${calendarSeasonalityResult.degree} para cada dia do mês.` +
        (calendarSeasonalityResult.isOptimized
          ? ' Foi utilizado otimizador para determinar pontos de mudança.'
          : ''),
    }
  } else if (result.name.startsWith('layer.calendarSeasonality.dayOfYear.')) {
    const calendarSeasonalityResult = result as ConditionalPolynomialCalendarSeasonalityResult
    return {
      name: 'Análise de sazonalidade de calendário por dia do ano',
      description:
        'Esta camada aplica um polinômio de grau configurável para cada dia do ano, de 1 a 365 ou 366 em anos bissextos, para capturar e descrever as variações comportamentais diárias ao longo do tempo. O modelo polinomial é ajustado de forma a proporcionar a melhor adaptação possível aos dados específicos de cada dia, facilitando uma compreensão detalhada das tendências sazonais diárias. Esta metodologia é particularmente valiosa para analisar e prever padrões que se manifestam em escalas temporais extremamente precisas, sobretudo em séries temporais de longo prazo.',
      settings:
        `Foram utilizados polinômios de grau ${calendarSeasonalityResult.degree} para cada dia do ano.` +
        (calendarSeasonalityResult.isOptimized
          ? ' Foi utilizado otimizador para determinar pontos de mudança.'
          : ''),
    }
  } else if (result.name.startsWith('layer.calendarSeasonality.monthOfYear.')) {
    const calendarSeasonalityResult = result as ConditionalPolynomialCalendarSeasonalityResult
    return {
      name: 'Análise de sazonalidade de calendário por mês do ano',
      description:
        'Esta camada configura um polinômio de grau ajustável para cada mês do ano, de janeiro a dezembro, para analisar e descrever as variações comportamentais específicas de cada período. A técnica matemática empregada envolve o ajuste de um modelo polinomial que minimiza as diferenças entre os dados observados e os estimados, permitindo uma representação precisa das tendências sazonais mensais. Esta abordagem é útil para identificar padrões recorrentes que podem influenciar decisões estratégicas ao longo do ano que variem de mês para mês.',
      settings:
        `Foram utilizados polinômios de grau ${calendarSeasonalityResult.degree} para cada mês do ano.` +
        (calendarSeasonalityResult.isOptimized
          ? ' Foi utilizado otimizador para determinar pontos de mudança.'
          : ''),
    }
  } else if (result.name.startsWith('layer.calendarSeasonality.year.')) {
    const calendarSeasonalityResult = result as ConditionalPolynomialCalendarSeasonalityResult
    return {
      name: 'Análise de sazonalidade de calendário por ano',
      description:
        'Esta camada ajusta um polinômio de grau configurável separadamente para cada ano da série, descrevendo a evolução ao longo dos dias dentro de cada ano.',
      settings:
        `Foram utilizados polinômios de grau ${calendarSeasonalityResult.degree} para cada ano.` +
        (calendarSeasonalityResult.isOptimized
          ? ' Foi utilizado otimizador para determinar pontos de mudança.'
          : ''),
    }
  } else if (result.name.startsWith('layer.calendarSeasonality.holiday.')) {
    const calendarSeasonalityResult = result as ConditionalPolynomialCalendarSeasonalityResult
    return {
      name: 'Análise de sazonalidade de calendário por feriado',
      description:
        'Esta camada ajusta um polinômio de grau configurável para cada feriado significativo, como Páscoa, Natal, Dia do Trabalhador, Carnaval, entre outros, visando descrever suas variações comportamentais específicas ao longo do tempo. A abordagem matemática envolve o ajuste de modelos polinomiais que melhor correspondem aos dados observados em cada feriado, permitindo uma análise detalhada das tendências que essas datas especiais podem apresentar. Este método é eficiente para explorar padrões sazonais associados a eventos pontuais e recorrentes no calendário.',
      settings:
        `Foram utilizados polinômios de grau ${calendarSeasonalityResult.degree} para cada feriado.` +
        (calendarSeasonalityResult.isOptimized
          ? ' Foi utilizado otimizador para determinar pontos de mudança.'
          : ''),
    }
  } else if (result.name.startsWith('layer.trend.')) {
    const polynomialResult = result as PolynomialResult
    return {
      name: 'Análise de tendência polinomial',
      description:
        'Esta camada modela os valores da série temporal através de um polinômio de grau configurável, proporcionando uma ferramenta robusta para estabelecer tendências a longo prazo. Matematicamente, os coeficientes polinomiais são determinados de modo a minimizar as discrepâncias entre os valores observados e os valores ajustados pelo modelo polinomial. Esse método é eficaz para capturar padrões complexos e tendências progressivas na série.',
      settings:
        `Foi utilizado um polinômio de grau ${polynomialResult.degree}.` +
        (polynomialResult.isOptimized
          ? ' Foi utilizado otimizador para determinar pontos de mudança.'
          : ''),
    }
  } else if (result.name.startsWith('layer.harmonicSeasonality.')) {
    const harmonicResult = result as HarmonicResult
    return {
      name: 'Análise de sazonalidade harmônica',
      description:
        'Esta camada emprega a análise harmônica para detectar e descrever padrões cíclicos na série temporal que podem não seguir a lógica do calendário. Em vez disso, ela identifica componentes de maior amplitude que revelam padrões recorrentes em intervalos variados, como ciclos quinzenais, semestrais ou qualquer outro. Esta técnica é valiosa para descobrir e interpretar dinâmicas cíclicas complexas emergentes nos dados, oferecendo insights sobre periodicidades que operam independentemente de marcos temporais tradicionais, usando senoides para a análise.',
      settings: harmonicResult.isOptimized
        ? `Foi utilizado otimizador para determinar pontos de mudança.`
        : '',
    }
  } else {
    return {
      name: `Análise desconhecida (${result.name})`,
    }
  }
}

export interface ProbeResult {
  fitStart: string
  fitEnd: string
  fitError: number
  predictionStart: string
  predictionEnd: string
  predictionError: number
  predictionY: number[]
}

export interface ComponentsResult {
  space: {
    geoType: string
    geoId: string | number
  }
  x: string[]
  y: number[]
  generated: number[]
  layersGenerated: number[][]
  layersInput: number[][]
  maxDate: string
  minDate: string
  predictionStart: string
  predictionY: number[]
  residual: number[]
  results: TimeAnalysisResult[]
  probesResult: ProbeResult[]
}

export interface TimeAnalysisActionResult {
  predictionStart: string | null
  predictionY: number[] | null
  results: TimeAnalysisResult[] | null
  generated: number[] | null
  residual: number[] | null
  layersGenerated: Array<Array<number | null>> | null
  layersInput: number[][] | null
  x?: string[] | null
  y?: number[] | null
  probesResult?: ProbeResult[] | null
}
